import { useEffect, useState } from 'react';
import { FaUniversity } from 'react-icons/fa';
import { toast } from 'react-toastify';
import SelectTextField, { OptionProps } from '../../../../molecules/SelectTextField';
import { contractsManagementApi, Creditor, CreditorPayload } from '../../../../../services/contractsManagementApi';

// ============================================
// CONSTANTS & TYPES
// ============================================

const CREDITOR_TYPE_LABELS: Record<string, string> = {
    INTERNAL_CREDITOR: 'Credor Interno',
    EXTERNAL_CREDITOR: 'Credor Externo',
};

interface CreditorSelectorProps {
    value: CreditorPayload | null;
    onChange: (creditor: CreditorPayload | null) => void;
    disabled?: boolean;
    /** Quando true, apresenta apenas os dados do credor (sem caixa de seleção).
     *  Usado em operações travadas: finalizadas, aditadas, canceladas, etc. */
    readOnly?: boolean;
    errorMessage?: string;
}

// ============================================
// HELPERS
// ============================================

const creditorToPayload = (creditor: Creditor): CreditorPayload => ({
    name: creditor.name,
    govTaxId: creditor.govTaxId,
    personId: creditor.personId,
    type: creditor.type,
});

const toOption = (creditor: Creditor): OptionProps => ({
    value: creditor.personId,
    label: `${creditor.name} - ${creditor.govTaxId}`,
});

const typeLabel = (type?: string): string =>
    (type && CREDITOR_TYPE_LABELS[type]) || type || '-';

// ============================================
// SUB-COMPONENTS
// ============================================

const CreditorDataCard = ({ value }: { value: CreditorPayload }) => (
    <div className="mt-4 flex items-start gap-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 shrink-0">
            <FaUniversity />
        </div>
        <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">{typeLabel(value.type)}</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{value.name}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
                CNPJ: <span className="font-medium text-gray-700 dark:text-gray-300">{value.govTaxId}</span>
            </p>
        </div>
    </div>
);

// ============================================
// MAIN COMPONENT
// ============================================

const CreditorSelector = ({ value, onChange, disabled = false, readOnly = false, errorMessage }: CreditorSelectorProps) => {
    const [creditors, setCreditors] = useState<Creditor[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        // Em modo somente leitura não há seleção, então não busca a lista de credores.
        if (readOnly) return;

        let active = true;
        setIsLoading(true);
        contractsManagementApi
            .listCreditors()
            .then((list) => {
                if (active) setCreditors(list);
            })
            .catch(() => {
                if (active) toast.error('Erro ao carregar credores');
            })
            .finally(() => {
                if (active) setIsLoading(false);
            });
        return () => {
            active = false;
        };
    }, [readOnly]);

    const handleSelect = (personId: string) => {
        if (!personId) {
            onChange(null);
            return;
        }
        const selected = creditors.find((c) => c.personId === personId);
        if (selected) onChange(creditorToPayload(selected));
    };

    if (readOnly) {
        return (
            <div>
                <h3 className="text-md font-semibold mt-4 mb-3 text-gray-700 dark:text-gray-200">Credor</h3>
                <div className="p-4 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600">
                    {value
                        ? <CreditorDataCard value={value} />
                        : <p className="text-sm text-gray-500 dark:text-gray-400">Nenhum credor informado</p>}
                </div>
            </div>
        );
    }

    return (
        <div>
            <h3 className="text-md font-semibold mt-4 mb-3 text-gray-700 dark:text-gray-200">Credor</h3>

            <div className="p-4 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600">
                <SelectTextField
                    id="creditor"
                    label="Credor (dono da operação) *"
                    value={value?.personId || ''}
                    items={creditors.map(toOption)}
                    isLoading={isLoading}
                    disabled={disabled || isLoading}
                    emptyLabel="Selecione um credor..."
                    errorMessage={errorMessage}
                    onChange={(e) => handleSelect(e.target.value)}
                />

                {value && <CreditorDataCard value={value} />}
            </div>
        </div>
    );
};

export default CreditorSelector;
